import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { acceptCategoryRule, acceptPayeeCleanup } from '#banksync/ai-rules';
import type {
  CategoryRuleSuggestion,
  PayeeCleanupSuggestion,
  RuleSuggestions,
} from '#banksync/ai-rules';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import { AiRuleSuggestionsCategoryRow } from './AiRuleSuggestionsCategoryRow';
import { AiRuleSuggestionsCleanupRow } from './AiRuleSuggestionsCleanupRow';

type Props = {
  suggestions: RuleSuggestions;
};

// sort grounded suggestions (has existing payee, not a guess) above guesses
function sortCleanups(
  items: PayeeCleanupSuggestion[],
): PayeeCleanupSuggestion[] {
  return [...items].sort((a, b) => {
    const score = (c: PayeeCleanupSuggestion) =>
      c.existingName && !c.guess ? 0 : c.guess ? 2 : 1;
    return score(a) - score(b);
  });
}

export function AiRuleSuggestionsModal({ suggestions }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [cleanups, setCleanups] = useState(() =>
    sortCleanups(suggestions.payeeCleanups),
  );
  const [catRules, setCatRules] = useState(suggestions.categoryRules);

  async function handleAcceptCleanup(item: PayeeCleanupSuggestion) {
    const key = item.variants.join('|');
    try {
      await acceptPayeeCleanup(item);
      setCleanups(prev => prev.filter(c => c.variants.join('|') !== key));
      dispatch(
        addNotification({
          notification: {
            type: 'message',
            message: t('Payee cleanup rule created for "{{name}}".', {
              name: item.existingName ?? item.newName ?? '',
            }),
          },
        }),
      );
    } catch {
      dispatch(
        addNotification({
          notification: {
            type: 'error',
            message: t(
              'Failed to create payee cleanup rule. Please try again.',
            ),
          },
        }),
      );
    }
  }

  function handleDismissCleanup(item: PayeeCleanupSuggestion) {
    const key = item.variants.join('|');
    setCleanups(prev => prev.filter(c => c.variants.join('|') !== key));
  }

  async function handleAcceptCategory(item: CategoryRuleSuggestion) {
    try {
      await acceptCategoryRule(item);
      setCatRules(prev => prev.filter(r => r.payeeId !== item.payeeId));
      dispatch(
        addNotification({
          notification: {
            type: 'message',
            message: t('Category rule created: {{payee}} -> {{category}}.', {
              payee: item.payeeName,
              category: item.categoryName,
            }),
          },
        }),
      );
    } catch {
      dispatch(
        addNotification({
          notification: {
            type: 'error',
            message: t('Failed to create category rule. Please try again.'),
          },
        }),
      );
    }
  }

  function handleDismissCategory(item: CategoryRuleSuggestion) {
    setCatRules(prev => prev.filter(r => r.payeeId !== item.payeeId));
  }

  return (
    <Modal name="ai-rule-suggestions">
      {({ state }) => (
        <View style={{ width: 'min(760px, 92vw)' }}>
          <ModalHeader
            title={t('AI rule suggestions')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          {/* scrollable area | two labeled sections */}
          <View
            style={{
              maxHeight: '60vh',
              overflowY: 'auto',
              marginBottom: 16,
            }}
          >
            {/* section 1: payee cleanups */}
            <View style={{ flexShrink: 0 }}>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.pageTextSubdued,
                  padding: '8px 8px 4px',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  borderBottom: '1px solid ' + theme.tableBorder,
                  flexShrink: 0,
                }}
              >
                <Trans>Payee cleanups</Trans>
              </Text>
              {cleanups.length === 0 ? (
                <Text
                  style={{
                    padding: 12,
                    fontSize: 13,
                    fontStyle: 'italic',
                    color: theme.pageTextSubdued,
                    flexShrink: 0,
                  }}
                >
                  <Trans>No payee cleanup suggestions</Trans>
                </Text>
              ) : (
                cleanups.map(item => (
                  <AiRuleSuggestionsCleanupRow
                    key={item.variants.join('|')}
                    item={item}
                    onAccept={() => void handleAcceptCleanup(item)}
                    onDismiss={() => handleDismissCleanup(item)}
                  />
                ))
              )}
            </View>

            {/* section 2: category rules */}
            <View style={{ flexShrink: 0, marginTop: 8 }}>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.pageTextSubdued,
                  padding: '8px 8px 4px',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  borderBottom: '1px solid ' + theme.tableBorder,
                  flexShrink: 0,
                }}
              >
                <Trans>Category rules</Trans>
              </Text>
              {catRules.length === 0 ? (
                <Text
                  style={{
                    padding: 12,
                    fontSize: 13,
                    fontStyle: 'italic',
                    color: theme.pageTextSubdued,
                    flexShrink: 0,
                  }}
                >
                  <Trans>No category rule suggestions</Trans>
                </Text>
              ) : (
                catRules.map(item => (
                  <AiRuleSuggestionsCategoryRow
                    key={item.payeeId}
                    item={item}
                    onAccept={() => void handleAcceptCategory(item)}
                    onDismiss={() => handleDismissCategory(item)}
                  />
                ))
              )}
            </View>
          </View>

          {/* footer */}
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <Button onPress={() => state.close()}>
              <Trans>Close</Trans>
            </Button>
          </View>
        </View>
      )}
    </Modal>
  );
}
